/* eslint-disable camelcase */
import React, { FC, useCallback, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { Appearance, FlatList, ListRenderItem, ListRenderItemInfo, ViewStyle, useWindowDimensions } from "react-native"
import { ListEmpty, Screen, Text } from "../../../../components"
// import { useStores } from "../../models"
import { color, nativeBaseTheme } from "../../../../theme"
import { useStores } from "../../../../models"
import { FarmStoreModel } from "../../../../models/app-store/farm-store/farm-store"
import { BatchStoreModel } from "../../../../models/app-store/farm-store/batch-store/batch-store"
import { SafeAreaView } from "react-native-safe-area-context"
import { View } from "native-base"
import numeral from "numeral"
import { translate } from "../../../../i18n"
import { InfoItem, ItemFooter, InfoWarningModal } from "../overview-feed-cost-list/overview-feed-cost-list-screen"
import { Currency } from "../../../../database"
import JalaAnalytic from "../../../../utils/jala-analytic"
import { useFocusEffect } from "@react-navigation/native"
import { getSnapshot } from "mobx-state-tree"

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  flex: 1,
}

const HEADER: ViewStyle = {
  borderTopWidth: 1,
  borderBottomWidth: 1,
  borderColor: color.palette.gray200,
  flexDirection: 'row',
  paddingVertical: 8,
  alignItems: 'center',
  backgroundColor: color.palette.white
}

const CONTENT_ITEM: ViewStyle = {
  borderBottomWidth: 1,
  borderColor: color.palette.gray200,
  flexDirection: 'row'
}

const Header: FC<{ colorScheme?: "light"|"dark", currency: string }> = observer(function Header(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), currency } = props;
  const { appStore: { farmStore }} = useStores();
  const { activeBatchStore } = farmStore || FarmStoreModel.create({})
  const { predictionCOGS, cycles, isFeedActualWithin4Days, loadingCyclePrediction } = activeBatchStore || BatchStoreModel.create({})

  return (
    <View paddingX="16px" paddingTop="8px" paddingBottom="15px">
      <Text text={farmStore.farm.name} preset="title3" marginBottom="4px" colorScheme={colorScheme} />
      <Text tx="overviewHPPEstimationListScreen.subtitle" txOptions={{currency}} preset="body3" colorScheme={colorScheme} color={nativeBaseTheme.colors.gray[colorScheme === 'light' ? '700' : '300']} />
      <View flexDirection="row" alignItems="center">
        <Text
          text={numeral(predictionCOGS).format('0,0')}
          preset="display1"
          marginRight="3px"
          colorScheme={colorScheme}
          color={loadingCyclePrediction && color.palette.dust500 || undefined}
        />
        <Text tx="overviewHPPEstimationListScreen.perKg" preset="display2" marginRight="8px" colorScheme={colorScheme} color={color.palette.dust500} />
      </View>
      <Text text={translate("overviewScreen.fromActivePonds", { count: cycles.length })} preset="body3" colorScheme={colorScheme} color={color.palette.dust500} />
      {!isFeedActualWithin4Days && <InfoWarningModal />}
    </View>
  )
})

const TableHeader: FC<{ colorScheme?: "light"|"dark", currency: string }> = (props) => {
  const { colorScheme, currency } = props;


  return (
    <View style={HEADER}>
      <View width="25%" paddingX="8px" alignItems="flex-start">
        <Text colorScheme={colorScheme} tx="overviewHPPEstimationListScreen.pond" preset="body3" color={color.palette.gray800} />
      </View>
      <View width="37%" paddingX="8px" alignItems="flex-end">
        <Text colorScheme={colorScheme} tx="overviewHPPEstimationListScreen.estHpp" txOptions={{currency}} preset="body3" color={color.palette.gray800} />
      </View>
      <View width="38%" paddingX="8px" alignItems="flex-end">
        <Text colorScheme={colorScheme} tx="overviewHPPEstimationListScreen.estBiomassa" preset="body3" color={color.palette.gray800} />
      </View>
    </View>
  )
}

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any> }> = observer(function Item(props) {
  const { colorScheme, info } = props
  const [cogs, setCogs] = useState(0)
  const [totalWeight, setTotalWeight] = useState(0)

  const { item: { age, pond_name, cycle_actuals, cycle_predictions} } = info;
  useEffect(() => {
    const actualCycle = cycle_actuals.find((c) => c.age === age) || {}
    if (actualCycle.cogs) {
      setCogs(actualCycle?.cogs)
      setTotalWeight(actualCycle?.total_weight)
    } else {
      const predictionCycle = cycle_predictions.find((c) => c.age === age) || {}
      setCogs(predictionCycle?.cogs)
      setTotalWeight(predictionCycle?.total_weight)
    }
  }, [age, cycle_actuals, cycle_predictions])

  return (
    <View style={CONTENT_ITEM}>
      <View width="25%" paddingX="8px" py="10px" alignItems="flex-start">
        <Text ellipsizeMode="middle" text={`${pond_name} (${age})`} preset="body3" colorScheme={colorScheme} numberOfLines={1} />
      </View>
      <View width="37%" paddingX="8px" py="10px" alignItems="flex-end" backgroundColor={color.palette.gray100}>
        <Text text={numeral(cogs).format('0,0')} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="38%" paddingX="8px" py="10px" alignItems="flex-end">
        <Text text={numeral(totalWeight).format('0,0')} preset="body3" colorScheme={colorScheme} />
      </View>
    </View>
  )
});

export const OverviewHppEstimationListScreen: FC<{ colorScheme?: "light" | "dark" }> = observer(function OverviewHppEstimationListScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;
  const { height } = useWindowDimensions();

  // Pull in one of our MST stores
  const { appStore: { farmStore }} = useStores();
  const { activeBatchStore, farm } = farmStore || FarmStoreModel.create({})
  const { cycles, loadingCyclePrediction } = activeBatchStore || BatchStoreModel.create({})
  const keyExtractor = useCallback((item, index) => `${item.id || item.started_at || index}`, [])
  const [showHeader, setShowHeader] = useState<boolean>(false)
  const [currency, setCurrency] = useState<string>()

  useEffect(() => {
    if (farm?.currency_id) {
      Currency.fetch({
        id__eq: farm?.currency_id
      }).then(models => {
        return models.map(model => model._attributes)
      }).then(values => {
        setCurrency(values.length ? values[0].symbol : farm?.currency_id)
      })
    }
  }, [farm])

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <Item info={info} colorScheme={colorScheme} />
  }, [colorScheme])

  const onRefresh = useCallback(() => {
    activeBatchStore.init()
  }, [activeBatchStore])

  useFocusEffect(useCallback(() => {
    JalaAnalytic.log('Mobile app: Home - Overview - Estimation COGS - View');
  }, []))

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      hideOnBlurred={false}
    >
      {showHeader && <TableHeader currency={currency} colorScheme={colorScheme} />}
      <FlatList
        data={getSnapshot(cycles)}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        ListHeaderComponent={<View backgroundColor={color.palette.white}>
          <Header currency={currency} colorScheme={colorScheme} />
          <InfoItem colorScheme={colorScheme}>
            <View flexDirection={"row"} alignItems={"center"}>
              <Text color={color.palette.orange700} preset="title5" colorScheme={colorScheme} tx="overviewHPPEstimationListScreen.infoCostFormula" />
              <View flexDirection={"column"} alignItems={"center"} marginLeft={"4px"}>
                <Text paddingY={"4px"} color={color.palette.orange700} preset="title5" colorScheme={colorScheme} tx="overviewHPPEstimationListScreen.infoCostFormula2" txOptions={{currency}} />
                <View width={200} backgroundColor={color.palette.orange700} height={"1px"} />
                <Text paddingY={"4px"} color={color.palette.orange700} preset="title5" colorScheme={colorScheme} tx="overviewHPPEstimationListScreen.infoCostFormula3" />
              </View>
            </View>
            </InfoItem>
          <TableHeader currency={currency} colorScheme={colorScheme} />
        </View>}
        ListEmptyComponent={<ListEmpty colorScheme={colorScheme} height={`${height - 270}px`} />}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
        onRefresh={onRefresh}
        refreshing={!!loadingCyclePrediction}
        onScroll={({nativeEvent}) => {
          setShowHeader(nativeEvent.contentOffset.y>200)
        }}
      />
      <ItemFooter colorScheme={colorScheme} />
    </Screen>
  )
})
